import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import * as yup from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { useState, type ReactNode } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { useCompanyApproveAssetRequestMutation } from "@/services/(company)/api.assets";
import { CircleCheckBig } from "lucide-react";

interface Props {
  children: ReactNode;
  requestId: number;
}

const FormSchema = yup.object({
  notes: yup.string().optional(),
});
type Fields = yup.InferType<typeof FormSchema>;

export const ActionAcceptConsumableAssetReqDialog = ({
  children,
  requestId,
}: Props) => {
  const { t } = useTranslation(["assets", "common"]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [acceptRequest, { isLoading: isAssigningAsset }] =
    useCompanyApproveAssetRequestMutation();

  const formik = useFormik<Fields>({
    initialValues: {
      notes: "",
    },
    validationSchema: FormSchema,
    onSubmit: async ({ notes }) => {
      const id = toast.loading(t("assets:processing"));
      await acceptRequest({ requestId, notes })
        .unwrap()
        .then((res) => {
          toast.success(res.message, { id });
          setIsOpen(false);
        })
        .catch((e) =>
          toast.error(e?.data?.errors?.message ?? e?.data?.message, { id }),
        );
    },
  });

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        formik.resetForm();
        setIsOpen(open);
      }}
    >
      <AlertDialogTrigger
        asChild
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={t("assets:approve_this_asset_request")}
      >
        {children}
      </AlertDialogTrigger>
      <AlertDialogContent asChild className="gap-0 p-0 overflow-hidden">
        <form
          onSubmit={formik.handleSubmit}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Colored header band */}
          <div className="bg-success-muted px-6 pt-6 pb-5 flex gap-4 items-start border-b border-success">
            <div className="shrink-0 flex items-center justify-center size-10 rounded-full bg-success">
              <CircleCheckBig className="text-success size-5" />
            </div>
            <AlertDialogHeader className="space-y-1 text-start">
              <AlertDialogTitle className="text-base text-success-700">
                {t("assets:approve_asset_request")}
              </AlertDialogTitle>
              <p className="text-sm text-success/80">
                {t(
                  "assets:the_employee_will_be_notified_once_the_request_is_approv",
                )}
              </p>
            </AlertDialogHeader>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <Field>
              <FieldLabel htmlFor={"notes" satisfies keyof Fields}>
                {t("assets:additional_notes")}{" "}
                <span className="text-muted-foreground font-normal">
                  ({t("common:labels.optional")})
                </span>
              </FieldLabel>
              <Textarea
                id={"notes" satisfies keyof Fields}
                className="min-h-28 mt-1.5 resize-none"
                value={formik.values.notes}
                onChange={formik.handleChange}
                placeholder={t(
                  "assets:add_any_additional_notes_for_the_employee",
                )}
                name={"notes" satisfies keyof Fields}
              />
              <FieldError>{formik.errors.notes}</FieldError>
            </Field>
          </div>

          <AlertDialogFooter className="px-6 pb-5 flex gap-3">
            <AlertDialogCancel
              type="reset"
              className="flex-1"
              disabled={isAssigningAsset}
            >
              {t("common:buttons.cancel")}
            </AlertDialogCancel>
            <Button
              type="submit"
              disabled={isAssigningAsset}
              className="flex-1 bg-success hover:bg-success"
            >
              {isAssigningAsset
                ? t("assets:approving")
                : t("assets:approve_request")}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
